import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { convertGoogleDriveUrl } from '../../utils/imageUtils';

interface ImageUploaderProps {
  label: string;
  onImageSelect: (file: File) => void;
  onUrlInput?: (url: string) => void;
  currentImage?: string;
}

export const ImageUploader = ({ label, onImageSelect, onUrlInput, currentImage }: ImageUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onImageSelect(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImageSelect(file);
      setShowUrlInput(false);
    }
  };

  const handleClick = () => {
    if (!showUrlInput) {
      fileInputRef.current?.click();
    }
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim() && onUrlInput) {
      const convertedUrl = convertGoogleDriveUrl(urlInput.trim());
      onUrlInput(convertedUrl);
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      
      {!showUrlInput ? (
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 cursor-pointer ${
            isDragging 
              ? 'border-blue-500 bg-blue-50' 
              : currentImage 
              ? 'border-gray-300 bg-white' 
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {currentImage ? (
            <div className="space-y-3">
              <img 
                src={currentImage} 
                alt={label} 
                className="max-h-48 mx-auto rounded-lg object-contain"
              />
              <p className="text-sm text-gray-600">クリックまたはドラッグして変更</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center">
                <Upload className="w-full h-full text-gray-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  クリックまたはドラッグ&ドロップ
                </p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, GIF (最大10MB)</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
          <div className="space-y-3">
            <input
              type="text"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="画像URLまたはGoogle Driveリンクをペースト"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleUrlSubmit();
                }
              }}
            />
            <div className="flex gap-2">
              <button
                onClick={handleUrlSubmit}
                disabled={!urlInput.trim()}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
              >
                URLを使用
              </button>
              <button
                onClick={() => {
                  setShowUrlInput(false);
                  setUrlInput('');
                }}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
